import { NavLink, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { navTree, navItems } from "../../config/navigation";
import { ArrowLeftOnRectangleIcon, BuildingOfficeIcon, BuildingStorefrontIcon, UserCircleIcon, ChevronRightIcon, ChevronLeftIcon, ChevronDownIcon, Bars3Icon, XMarkIcon, TvIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../providers/auth-provider";
import UserProfileModal from "./UserProfileModal";
import { useApiClient } from "../../lib/api";
import { useConfig } from "../../providers/config-provider";
import { UI_EVENTS } from "../../config";
import { writePanelScope } from "../../utils/panelScopeStorage";
import clsx from "clsx";

const isAllowed = (entry, role, user) => {
  if (entry.requiresSubscription && !user?.hasSubscription) {
    return false;
  }
  if (!entry.roles || !entry.roles.length) {
    return true;
  }
  if (role === "AdminMaster") {
    return true;
  }
  return entry.roles.includes(role);
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const api = useApiClient();
  const location = useLocation();
  const { companyId, restaurantId, setCompanyId, setRestaurantId, setOperationalScope } = useConfig();
  
  const [collapsed, setCollapsed] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState([]);
  const [profile, setProfile] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const restaurantIdRef = useRef(restaurantId);

  useEffect(() => {
    restaurantIdRef.current = restaurantId;
  }, [restaurantId]);

  // Group Nav Tree based on User Roles
  const allowedNavTree = useMemo(() => {
    if (!user) return [];
    
    return navTree.map(group => {
       if (group.type === "group") {
         const allowedChildren = (group.children || []).filter(c => isAllowed(c, user?.role, user));
         if (allowedChildren.length > 0) return { ...group, children: allowedChildren };
         return null;
       }
       return isAllowed(group, user?.role, user) ? group : null;
    }).filter(Boolean);
  }, [user]);

  // Flattened for mobile layout
  const allowedNavItems = useMemo(() => navItems.filter((i) => isAllowed(i, user?.role, user)), [user]);

  const restaurantOptions = useMemo(() => {
    const raw = profile?.restaurants ?? user?.restaurants ?? [];
    if (!companyId) {
      return raw;
    }
    return raw.filter((restaurant) => !restaurant.companyId || restaurant.companyId === companyId);
  }, [user, profile, companyId]);

  const toggleGroup = (groupName) => {
    setOpenGroups((prev) =>
      prev.includes(groupName) ? prev.filter((name) => name !== groupName) : [...prev, groupName]
    );
  };

  const loadProfile = useCallback(async () => {
    try {
      const response = await api.get("/api/v1/company/profile");
      const data = response.data;
      setProfile(data);
      const allowed = ((user?.role === "AdminMaster" || user?.role === "Admin") ? data?.restaurants : user?.restaurants) ?? [];
      const scoped = companyId
        ? allowed.filter((restaurant) => !restaurant.companyId || restaurant.companyId === companyId)
        : allowed;

      const sortedScoped = [...scoped].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
      const currentRestaurantId = restaurantIdRef.current;
      const current = currentRestaurantId && sortedScoped.some((restaurant) => restaurant.id === currentRestaurantId)
        ? currentRestaurantId
        : null;
      const fallback = current || sortedScoped[0]?.id || null;
      
      if (fallback) {
        setRestaurantId(fallback);
      } else {
        setRestaurantId(null);
      }
    } catch (error) {
      console.error(error);
    }
  }, [api, user, companyId, setRestaurantId]);

  const loadCompanies = useCallback(async () => {
    try {
      setCompaniesLoading(true);
      const response = await api.get("/api/v1/admin/companies", {
        params: {
          includeInactive: false,
          page: 1,
          pageSize: 200,
          sortBy: "name"
        }
      });
      const payload = response.data;
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : [];
      const normalized = list.map((item) => ({ id: item.id, name: item.name }));
      setCompanies(normalized);

      const hasCurrentCompany = companyId ? normalized.some((item) => item.id === companyId) : false;
      if ((!companyId || !hasCurrentCompany) && normalized.length > 0) {
        setCompanyId(normalized[0].id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCompaniesLoading(false);
    }
  }, [api, companyId, setCompanyId]);

  useEffect(() => {
    if (!user) return;
    loadProfile();
  }, [user, companyId, loadProfile]);

  useEffect(() => {
    if (!user) return;

    if (user.isAdminMaster) {
      const authCompanies = Array.isArray(user.companies)
        ? user.companies.map((item) => ({ id: item.id, name: item.name }))
        : [];

      if (authCompanies.length) {
        setCompanies(authCompanies);
        const hasCurrentCompany = companyId ? authCompanies.some((item) => item.id === companyId) : false;
        if ((!companyId || !hasCurrentCompany) && authCompanies[0]?.id) {
          setCompanyId(authCompanies[0].id);
        }
      }

      loadCompanies();
      return;
    }

    if (user.companyId && !companyId) {
      setCompanyId(user.companyId);
    }
  }, [user, companyId, loadCompanies, setCompanyId]);

  useEffect(() => {
    if (!user?.isAdminMaster) {
      return undefined;
    }

    const refreshCompanies = () => {
      loadCompanies();
    };

    window.addEventListener("focus", refreshCompanies);
    return () => {
      window.removeEventListener("focus", refreshCompanies);
    };
  }, [user?.isAdminMaster, loadCompanies]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const refreshScope = () => {
      if (user?.isAdminMaster) {
        loadCompanies();
      }
      loadProfile();
    };

    window.addEventListener(UI_EVENTS.scopeRefresh, refreshScope);
    return () => {
      window.removeEventListener(UI_EVENTS.scopeRefresh, refreshScope);
    };
  }, [user?.isAdminMaster, loadCompanies, loadProfile]);

  const handleCompanyChange = (event) => {
    const selected = event.target.value;
    const normalized = selected && selected.length ? selected : null;
    setCompanyId(normalized);
    setProfile(null);
  };

  const handleRestaurantChange = (event) => {
    const selected = event.target.value;
    const normalized = selected && selected.length ? selected : null;
    if (normalized && !restaurantOptions.some((r) => r.id === normalized)) return;
    setRestaurantId(normalized);
  };

  const handleLogout = () => {
    logout();
  };

  const handleOpenPanel = () => {
    const companyName = companies.find((c) => c.id === companyId)?.name || profile?.name || user?.companyName || "";
    const restaurant = restaurantOptions.find((r) => r.id === restaurantId);
    const restaurantLabel = restaurant?.name || "";
    setOperationalScope(companyId, restaurantId);
    writePanelScope({
      companyId,
      companyName,
      restaurantId,
      restaurantName: restaurantLabel,
    });
    window.open("/painel/login", "_blank", "noopener");
  };

  const isCurrentPath = (path) => {
    if (path === "/app") {
      return location.pathname === "/app";
    }
    return location.pathname.startsWith(path);
  };

  const mobilePrimaryItems = useMemo(() => {
    const desiredPaths = ["/app/calls", "/app/tables", "/app/areas"];

    return desiredPaths
      .map((path) => allowedNavItems.find((item) => item.to === path))
      .filter(Boolean);
  }, [allowedNavItems]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <UserProfileModal open={isProfileOpen} onClose={() => setIsProfileOpen(false)} user={user} />
      
      {/* Sidebar Desktop */}
      <aside className={clsx(
          "hidden h-screen shrink-0 flex-col overflow-hidden border-r border-slate-800 bg-slate-900 text-slate-200 shadow-xl md:sticky md:top-0 md:flex print:hidden transition-all duration-300 ease-in-out z-50",
          collapsed ? "w-20" : "w-72"
      )}>
        <div className={clsx("border-b border-slate-800/70 px-6 py-6 min-h-[85px] flex flex-col justify-center", collapsed && "px-2 text-center")}>
          {collapsed ? (
              <h1 className="text-xl font-bold text-white">WC</h1>
          ) : (
              <>
                  <h1 className="text-lg font-semibold tracking-tight text-white">
                    WaiterCall
                  </h1>
                  <p className="mt-1 text-xs text-slate-400">
                    Atendimento ágil
                  </p>
              </>
          )}
        </div>

        <nav className="custom-scrollbar min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-3 py-3">
          {allowedNavTree.map((entry) => {
            if (entry.children) {
              const isOpen = openGroups.includes(entry.name);
              const isActive = entry.children.some((child) =>
                location.pathname.startsWith(child.to)
              );
              return (
                <div key={entry.name} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleGroup(entry.name)}
                    className={clsx(
                      "flex w-full items-center rounded-lg px-3 py-2 text-sm font-semibold transition",
                      collapsed ? "justify-center" : "justify-between",
                      isActive
                        ? "bg-slate-800 text-white"
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    )}
                    title={collapsed ? entry.name : ""}
                  >
                    <span className="flex items-center gap-3">
                      <entry.icon className="h-4 w-4" />
                      {!collapsed && entry.name}
                    </span>
                    {!collapsed && (
                      <ChevronDownIcon
                          className={clsx(
                          "h-4 w-4 transform transition-transform",
                          isOpen ? "rotate-0" : "-rotate-90"
                          )}
                      />
                    )}
                  </button>
                  <div
                    className={clsx(
                      "space-y-1 border-l border-slate-800 pl-6",
                      isOpen && !collapsed ? "block" : "hidden"
                    )}
                  >
                    {entry.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.to === "/app"}
                        className={({ isActive }) =>
                          clsx(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition",
                            isActive
                              ? "bg-indigo-500 text-white shadow-sm"
                              : "text-slate-300 hover:bg-slate-800 hover:text-white"
                          )
                        }
                      >
                        {child.icon && <child.icon className="h-4 w-4" />}
                        {child.name}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.to === "/app"}
                className={({ isActive }) =>
                  clsx(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition",
                    collapsed ? "justify-center" : "justify-start",
                    isActive
                      ? "bg-indigo-500 text-white shadow-sm"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )
                }
                title={collapsed ? entry.name : ""}
              >
                {entry.icon && <entry.icon className="h-4 w-4 shrink-0" />}
                {!collapsed && <span className="truncate">{entry.name}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-slate-800/80 bg-slate-900/60">
          <div className="px-3 py-2">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="flex w-full items-center justify-center rounded-lg px-3 py-2 text-slate-400 hover:bg-slate-800 hover:text-white transition"
              title={collapsed ? "Expandir" : "Recolher"}
            >
              {collapsed ? <ChevronRightIcon className="h-5 w-5" /> : (
                  <div className="flex items-center gap-2">
                      <ChevronLeftIcon className="h-5 w-5" />
                      <span className="text-xs">Recolher Menu</span>
                  </div>
              )}
            </button>
          </div>

          {!collapsed && (
            <div className="border-t border-slate-800 px-6 py-3 space-y-3">
              {user?.isAdminMaster ? (
                <div>
                  <label className="mb-1 flex items-center text-xs text-slate-400"><BuildingOfficeIcon className="w-3 h-3 mr-1" /> Empresa Ativa</label>
                  <select
                    className="w-full rounded border border-slate-700 bg-slate-900 p-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
                    value={companyId || ""}
                    onChange={handleCompanyChange}
                    disabled={companiesLoading}
                  >
                    {companiesLoading ? <option value="">Carregando...</option> : null}
                    {!companiesLoading && !companies.length ? <option value="">Nenhuma empresa</option> : null}
                    {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                   <label className="mb-1 flex items-center text-xs text-slate-400"><BuildingOfficeIcon className="w-3 h-3 mr-1" /> Empresa</label>
                   <p className="text-xs font-medium text-white truncate">{profile?.name || user?.companyName || "-"}</p>
                </div>
              )}

              <div>
                <label className="mb-1 flex items-center text-xs text-slate-400"><BuildingStorefrontIcon className="w-3 h-3 mr-1" /> Restaurante</label>
                {restaurantOptions.length ? (
                  <select
                    className="w-full rounded border border-slate-700 bg-slate-900 p-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
                    value={restaurantId || ""}
                    onChange={handleRestaurantChange}
                  >
                    {restaurantOptions.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs text-slate-500 block py-1">Sem restaurantes</span>
                )}
              </div>

              {restaurantId && (
                <button
                  type="button"
                  onClick={handleOpenPanel}
                  className="mt-3 flex w-full items-center gap-2 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-2 text-xs font-medium text-indigo-300 transition hover:bg-indigo-500/20 hover:text-indigo-200"
                >
                  <TvIcon className="h-4 w-4 shrink-0" />
                  <span className="truncate">Abrir Painel de Chamadas</span>
                </button>
              )}
            </div>
          )}

          {collapsed && restaurantId && (
            <div className="border-t border-slate-800 px-2 py-2 flex justify-center">
              <button
                type="button"
                onClick={handleOpenPanel}
                title="Abrir Painel de Chamadas"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-indigo-400 transition hover:bg-indigo-500/20 hover:text-indigo-300"
              >
                <TvIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          <div
            className={clsx(
              "border-t border-slate-800 py-4",
              collapsed ? "px-2 text-center" : "px-6 text-center"
            )}
          >
            {!collapsed ? (
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={() => setIsProfileOpen(true)}
                    className="max-w-full truncate text-sm font-medium text-white transition hover:text-indigo-300"
                  >
                    {user?.name || user?.email}
                  </button>
                  <p className="mt-0.5 truncate text-xs text-slate-400">
                    {user?.role || "Usuário"}
                  </p>
                  <button
                    onClick={handleLogout}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
                  >
                    <ArrowLeftOnRectangleIcon className="h-4 w-4" />
                    Sair
                  </button>
                  <p className="mt-3 text-xs text-slate-500">Versão v0.1.0</p>
                </div>
            ) : (
                <div className="flex flex-col items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setIsProfileOpen(true)}
                    title={user?.name || user?.email}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-indigo-400 hover:bg-slate-700 transition"
                  >
                    <span className="text-xs font-bold">
                        {(user?.name || user?.email || "U").substring(0, 2).toUpperCase()}
                    </span>
                  </button>
                  <button
                    onClick={handleLogout}
                    title="Sair"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-600 text-white transition hover:bg-red-700"
                  >
                    <ArrowLeftOnRectangleIcon className="h-4 w-4" />
                  </button>
                  <p className="mt-1 text-[10px] text-slate-500">v0.1.0</p>
                </div>
            )}
          </div>
        </div>
      </aside>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200/80 bg-white/95 shadow-[0_-6px_18px_rgba(15,23,42,0.08)] backdrop-blur md:hidden print:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Navegação móvel"
      >
        <div className="flex h-16">
          {mobilePrimaryItems.map((item) => (
            <NavLink
              key={`mobile-bottom-${item.to}`}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  "flex flex-1 flex-col items-center justify-center px-2 text-[11px] font-medium transition",
                  isActive ? "text-indigo-600" : "text-slate-500"
                )
              }
            >
              {item.icon && <item.icon className="h-5 w-5" />}
              <span className="mt-1 max-w-[72px] truncate">{item.name}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={clsx(
              "flex flex-1 flex-col items-center justify-center px-2 text-[11px] font-medium transition",
              mobileMenuOpen ? "text-indigo-600" : "text-slate-500"
            )}
          >
            <Bars3Icon className="h-5 w-5" />
            <span className="mt-1">Menu</span>
          </button>
        </div>
      </nav>

      <div
        className={clsx(
          "fixed inset-0 z-50 md:hidden print:hidden",
          mobileMenuOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
      >
        <button
          type="button"
          aria-label="Fechar menu móvel"
          onClick={() => setMobileMenuOpen(false)}
          className={clsx(
            "absolute inset-0 bg-slate-950/55 transition-opacity duration-300",
            mobileMenuOpen ? "opacity-100" : "opacity-0"
          )}
        />

        <aside
          className={clsx(
            "absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col border-l border-slate-800 bg-slate-950 text-slate-100 shadow-2xl transition-transform duration-300 ease-out",
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
            <div>
              <p className="text-sm font-semibold text-white">Menu</p>
              <p className="text-xs text-slate-400">Orion WaiterCall</p>
            </div>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-800 hover:text-white"
              aria-label="Fechar painel lateral"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="custom-scrollbar flex-1 overflow-y-auto px-4 py-4">
            <section>
              <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Navegação</p>
              <div className="mt-2 space-y-2">
                {allowedNavTree.map((entry) => {
                  if (entry.children) {
                    const isOpen = openGroups.includes(entry.name);
                    const isActive = entry.children.some((child) => isCurrentPath(child.to));

                    return (
                      <div key={`mobile-group-${entry.name}`} className="rounded-2xl border border-slate-800/80 bg-slate-900/60">
                        <button
                          type="button"
                          onClick={() => toggleGroup(entry.name)}
                          className={clsx(
                            "flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-semibold transition",
                            isActive ? "bg-slate-800 text-white" : "text-slate-200 hover:bg-slate-800 hover:text-white"
                          )}
                        >
                          <span className="flex items-center gap-3">
                            <entry.icon className="h-5 w-5" />
                            <span className="truncate">{entry.name}</span>
                          </span>
                          <ChevronDownIcon className={clsx("h-4 w-4 transform transition-transform", isOpen ? "rotate-0" : "-rotate-90")} />
                        </button>

                        <div className={clsx("space-y-1 px-2 pb-2 pt-1", isOpen ? "block" : "hidden")}>
                          {entry.children.map((child) => (
                            <NavLink
                              key={`mobile-child-${child.to}`}
                              to={child.to}
                              onClick={() => setMobileMenuOpen(false)}
                              className={clsx(
                                "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition pl-4",
                                isCurrentPath(child.to)
                                  ? "bg-indigo-500 text-white shadow-sm"
                                  : "text-slate-200 hover:bg-slate-800 hover:text-white"
                              )}
                            >
                              <child.icon className="h-4 w-4" />
                              <span className="truncate">{child.name}</span>
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <NavLink
                      key={`mobile-single-${entry.to}`}
                      to={entry.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={clsx(
                        "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition",
                        isCurrentPath(entry.to)
                          ? "bg-indigo-500 text-white shadow-sm"
                          : "text-slate-200 hover:bg-slate-800 hover:text-white"
                      )}
                    >
                      <entry.icon className="h-5 w-5" />
                      <span className="truncate">{entry.name}</span>
                    </NavLink>
                  );
                })}
              </div>
            </section>
          </div>

          <div className="border-t border-slate-800 px-5 py-4 text-center">
            {user?.isAdminMaster ? (
              <div className="mb-4">
                <label className="mb-1 flex items-center text-xs text-slate-400"><BuildingOfficeIcon className="w-3 h-3 mr-1" /> Empresa Ativa</label>
                <select
                  className="w-full rounded border border-slate-700 bg-slate-900 p-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
                  value={companyId || ""}
                  onChange={handleCompanyChange}
                  disabled={companiesLoading}
                >
                  {companiesLoading ? <option value="">Carregando...</option> : null}
                  {!companiesLoading && !companies.length ? <option value="">Nenhuma empresa</option> : null}
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="mb-4 text-left">
                <label className="mb-1 flex items-center text-xs text-slate-400"><BuildingOfficeIcon className="w-3 h-3 mr-1" /> Empresa</label>
                <p className="truncate text-xs font-medium text-white">{profile?.name || user?.companyName || "-"}</p>
              </div>
            )}

            <div className="mb-4 text-left">
              <label className="mb-1 flex items-center text-xs text-slate-400"><BuildingStorefrontIcon className="w-3 h-3 mr-1" /> Restaurante</label>
              {restaurantOptions.length ? (
                <select
                  className="w-full rounded border border-slate-700 bg-slate-900 p-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
                  value={restaurantId || ""}
                  onChange={handleRestaurantChange}
                >
                  {restaurantOptions.map((restaurant) => (
                    <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>
                  ))}
                </select>
              ) : (
                <span className="block py-1 text-xs text-slate-500">Sem restaurantes</span>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                setIsProfileOpen(true);
              }}
              className="mx-auto block max-w-full truncate text-sm font-medium text-white transition hover:text-indigo-300"
            >
              {user?.name || user?.email}
            </button>
            <p className="mt-0.5 truncate text-xs text-slate-400">{user?.role || "Usuário"}</p>
            {restaurantId && (
              <button
                type="button"
                onClick={() => { setMobileMenuOpen(false); handleOpenPanel(); }}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-2.5 text-sm font-medium text-indigo-300 transition hover:bg-indigo-500/20"
              >
                <TvIcon className="h-4 w-4" />
                Abrir Painel de Chamadas
              </button>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
            >
              <ArrowLeftOnRectangleIcon className="h-4 w-4" />
              Sair
            </button>
            <p className="mt-3 text-xs text-slate-500">Versão v0.1.0</p>
          </div>
        </aside>
      </div>
    </>
  );
}
